import { Helmet } from "react-helmet-async";
import { Spinner } from "../../../atoms/Spinner";
import useAuth from "../../../../hooks/useAuth";
import SwaggerInfos from "./SwaggerInfos";
import EntityStatusChanger from "./EntityStatusChanger";
import EntityPreferencesPreview from "./EntityPreferencesPreview";
import EntityPreferences from "../entities/EntityPreferences";
import EmailSenderSettings from "../entities/EmailSenderSettings";
import WhatsAppChannelSettings from "./WhatsAppChannelSettings";
import ExportData from "./ExportData";
import CleanDatabaseToggle from "./CleanDatabaseToggle";
import RoleMatrixEditor from "./RoleMatrixEditor";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import { CustomButton } from "../../../atoms/CustomButton";
import CollapsibleHowItWorks from "../../../common/CollapsibleHowItWorks";
import {
  HiOutlineStatusOnline,
  HiOutlineInformationCircle,
  HiOutlineCog,
  HiOutlineMail,
  HiOutlineChatAlt2,
  HiOutlineDatabase,
  HiOutlineShieldCheck,
  HiOutlineSparkles,
  HiOutlineDownload,
  HiOutlineTrash,
} from "react-icons/hi";
import {
  useGetEntity,
  useGetEntityPreferencesByPath,
  useGetUserData,
} from "../../../../hooks/entity";
import DeleteOwnerModal from "../../../modals/DeleteOwnerModal";

const Settings = () => {
  const [t] = useTranslation();
  const { customer_identification, user, userEndedSubscription } = useAuth();
  const permissions = usePermissions(user);
  const { state } = useLocation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const isOwnerAccount = permissions.isOwner();

  const canSeeBusinessSettings = permissions.can(
    Cap.ENTITY_SETTINGS,
    Level.VIEW
  );
  const canSeeApis = permissions.can(Cap.APIS, Level.VIEW);
  const canSeeWhatsappChannel = permissions.can(Cap.SEND_WHATSAPP, Level.WRITE);
  const canSeeServiceConfig = canSeeBusinessSettings || canSeeApis;
  const canSeeRoles = permissions.can(Cap.ROLE_MANAGEMENT, Level.VIEW);
  const canSeeDataExport =
    permissions.hasFeatureAccess("EXPORT_DATA") &&
    permissions.can(Cap.EXPORT_DATA, Level.VIEW);

  const { data, isSuccess, isLoading, isError, refetch } =
    useGetEntityPreferencesByPath(
      customer_identification,
      user.entity_identification,
      [
        "web_site_login_page",
        "web_site_reset_password_page",
        "cdn_logo_project_path",
        "webhook_url",
        "password_validation_regex_level",
        "allowed_user_email_domain",
        "email_address_sender",
      ].join(",")
    );

  const {
    data: entityData,
    isSuccess: entityIsSuccess,
    isLoading: entityIsLoading,
    isError: entityIsError,
    refetch: entityRefetch,
  } = useGetEntity(customer_identification, user.entity_identification, {
    enabled: isSuccess,
  });

  const { data: userData, isSuccess: isSuccessUserData } = useGetUserData(
    customer_identification,
    user.entity_identification,
    customer_identification
  );

  const SECTION_TO_ACCORDION: Record<string, string> = {
    "input-service-status": "acc-service-status",
    "input-logo": "acc-service-information",
    linksConfig: "acc-service-configuration",
    emailSenderConfig: "acc-email-sender",
    whatsappChannel: "acc-whatsapp-channel",
  };
  const [openSignals, setOpenSignals] = useState<Record<string, number>>({});

  const openAccordion = (accordionId: string) => {
    setOpenSignals((prev) => ({
      ...prev,
      [accordionId]: (prev[accordionId] || 0) + 1,
    }));
    window.setTimeout(() => {
      document
        .getElementById(accordionId)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
  };

  useEffect(() => {
    if (!entityIsSuccess) return;
    const sectionId = state?.section || window.location.hash.slice(1);
    if (!sectionId) return;

    const accId = SECTION_TO_ACCORDION[sectionId] || null;
    if (accId) {
      setOpenSignals((prev) => ({ ...prev, [accId]: (prev[accId] || 0) + 1 }));
      window.setTimeout(() => {
        const elm =
          document.getElementById(accId) || document.getElementById(sectionId);
        if (elm) elm.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    } else {
      const elm = document.getElementById(sectionId);
      if (elm) elm.scrollIntoView({ behavior: "smooth" });
    }
  }, [entityIsSuccess, state]);

  let entityContent;

  if (entityIsLoading)
    entityContent = (
      <span translate="no">
        <Spinner size="sm" />
      </span>
    );

  if (entityIsError)
    entityContent = (
      <CustomButton
        variant="secondary"
        onClick={() => entityRefetch()}
        title={t("common.retry")}
      />
    );

  let entityPreferencesContent;
  let emailSenderContent;

  if (isLoading) {
    entityPreferencesContent = (
      <span translate="no">
        <Spinner size="sm" />
      </span>
    );
    emailSenderContent = entityPreferencesContent;
  }

  if (isError) {
    entityPreferencesContent = (
      <CustomButton
        variant="secondary"
        onClick={() => refetch()}
        title={t("common.retry")}
      />
    );
    emailSenderContent = entityPreferencesContent;
  }

  if (entityIsSuccess && isSuccess) {
    entityContent = (
      <EntityPreferencesPreview
        entityPreferencesData={data}
        entityData={entityData}
      />
    );
    entityPreferencesContent = (
      <EntityPreferences
        data={data}
        user_identification={customer_identification}
        entity_identification={user.entity_identification}
      />
    );
    emailSenderContent = (
      <EmailSenderSettings
        data={data}
        user_identification={customer_identification}
        entity_identification={user.entity_identification}
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>Settings | Super User Manager</title>
        <meta name="description" content="Settings panel" />
        <link
          rel="canonical"
          href={`${process.env.REACT_APP_HOST_URL}/control-panel/settings`}
        />
      </Helmet>

      <div className="container mx-auto py-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-gray-900">
              {t("dashboard.develop.title")}
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              {t("dashboard.settings.pageSubtitle")}
            </p>
          </div>
        </div>

        <div className="flex w-full flex-col gap-3">
          {canSeeBusinessSettings && (
            <CollapsibleHowItWorks
              collapsible
              size="lg"
              className="w-full"
              id="acc-service-status"
              icon={
                <HiOutlineStatusOnline size={20} className="text-gray-500" />
              }
              openSignal={openSignals["acc-service-status"]}
              title={t("dashboard.settings.accordion.serviceStatus")}
            >
              <div id="input-service-status">
                <EntityStatusChanger />
              </div>
            </CollapsibleHowItWorks>
          )}

          {canSeeBusinessSettings && (
            <CollapsibleHowItWorks
              collapsible
              size="lg"
              className="w-full"
              id="acc-service-information"
              icon={
                <HiOutlineInformationCircle
                  size={20}
                  className="text-gray-500"
                />
              }
              openSignal={openSignals["acc-service-information"]}
              title={t("dashboard.settings.accordion.serviceInformation")}
            >
              {entityContent}
            </CollapsibleHowItWorks>
          )}

          {canSeeServiceConfig && (
            <CollapsibleHowItWorks
              collapsible
              size="lg"
              className="w-full"
              id="acc-service-configuration"
              icon={<HiOutlineCog size={20} className="text-gray-500" />}
              openSignal={openSignals["acc-service-configuration"]}
              title={t("dashboard.settings.accordion.serviceConfiguration")}
            >
              <div id="linksConfig">{entityPreferencesContent}</div>
            </CollapsibleHowItWorks>
          )}

          {canSeeApis && (
            <CollapsibleHowItWorks
              collapsible
              size="lg"
              className="w-full"
              id="acc-email-sender"
              icon={<HiOutlineMail size={20} className="text-gray-500" />}
              openSignal={openSignals["acc-email-sender"]}
              title={t("dashboard.settings.accordion.emailSender")}
            >
              <div id="emailSenderConfigSection">{emailSenderContent}</div>
            </CollapsibleHowItWorks>
          )}

          {canSeeWhatsappChannel && (
            <CollapsibleHowItWorks
              collapsible
              size="lg"
              className="w-full"
              id="acc-whatsapp-channel"
              icon={<HiOutlineChatAlt2 size={20} className="text-gray-500" />}
              openSignal={openSignals["acc-whatsapp-channel"]}
              title={t("dashboard.settings.accordion.whatsappChannel")}
            >
              <div id="whatsappChannel">
                <WhatsAppChannelSettings onOpenSection={openAccordion} />
              </div>
            </CollapsibleHowItWorks>
          )}

          {isOwnerAccount && (
            <CollapsibleHowItWorks
              collapsible
              size="lg"
              className="w-full"
              icon={<HiOutlineDatabase size={20} className="text-gray-500" />}
              title={t("dashboard.settings.accordion.cleanDatabase")}
            >
              <CleanDatabaseToggle />
            </CollapsibleHowItWorks>
          )}

          {canSeeRoles && (
            <CollapsibleHowItWorks
              collapsible
              size="lg"
              className="w-full"
              icon={
                <HiOutlineShieldCheck size={20} className="text-gray-500" />
              }
              title={t("dashboard.settings.accordion.rolesPermissions")}
            >
              <RoleMatrixEditor />
            </CollapsibleHowItWorks>
          )}

          {canSeeApis && isSuccessUserData && (
            <CollapsibleHowItWorks
              collapsible
              size="lg"
              className="w-full"
              icon={<HiOutlineSparkles size={20} className="text-gray-500" />}
              title={t("dashboard.settings.accordion.buildWithAi")}
            >
              <SwaggerInfos query={userData} disabled={userEndedSubscription} />
            </CollapsibleHowItWorks>
          )}

          {canSeeDataExport && (
            <CollapsibleHowItWorks
              collapsible
              size="lg"
              className="w-full"
              icon={<HiOutlineDownload size={20} className="text-gray-500" />}
              title={t("dashboard.settings.accordion.dataExport")}
            >
              <ExportData />
            </CollapsibleHowItWorks>
          )}

          {isOwnerAccount && (
            <CollapsibleHowItWorks
              collapsible
              size="lg"
              className="w-full"
              icon={<HiOutlineTrash size={20} className="text-red-500" />}
              title={t("dashboard.settings.accordion.deleteAccount")}
            >
              <div className="flex flex-col items-start gap-4">
                <p className="self-stretch text-sm text-gray-600">
                  {t("dashboard.deleteOwner.key6")}
                </p>
                <CustomButton
                  variant="danger"
                  className="self-end"
                  onClick={() => setShowDeleteModal(true)}
                  title={t("dashboard.deleteOwner.key5")}
                />
              </div>
            </CollapsibleHowItWorks>
          )}
        </div>
      </div>

      {showDeleteModal && (
        <DeleteOwnerModal
          showDeleteModal={showDeleteModal}
          setShowDeleteModal={setShowDeleteModal}
        />
      )}
    </>
  );
};

export default Settings;
